import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import config from '@config';

import {
  InstagramOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import xOutlinedNegativeIcon from '../../assets/icons/x-outlined-negative.svg';

const scoreImages = {
  default: {
    width: 1080,
    height: 1080,
    background: 'puntos_prode-1-1.png',
    textBox: {
      x: 450,
      y: 500,
      width: 550,
      height: 220,
      maxFontSize: 78,
    },
  },
  instagram: {
    width: 1080,
    height: 1920,
    background: 'puntos_prode-9-16.png',
    textBox: {
      x: 205,
      y: 715,
      width: 670,
      height: 270,
      maxFontSize: 88,
    },
  },
};

const getScoreLines = (username, score) => {
  const user = username.startsWith('@') ? username : `@${username}`;
  return [`${user} sum\u00f3`, `${score || 0} puntos`];
};

const getMessage = (score) => {
  return `\u26bd AC\u00c1 LA \u00daNICA PERSONA QUE SABE DE F\u00daTBOL SOY YO. \ud83c\udfaf Sum\u00e9 ${
    score || 0
  } puntos en el Prode de P\u00e1gina|12 y estoy cada vez m\u00e1s cerca del 0km \ud83d\ude97 \u00bfTe pens\u00e1s que pod\u00e9s ganarme?`;
};

const getImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

const drawCoverImage = (context, image, canvasWidth, canvasHeight) => {
  const scale = Math.max(canvasWidth / image.width, canvasHeight / image.height);
  const imageWidth = image.width * scale;
  const imageHeight = image.height * scale;
  const x = (canvasWidth - imageWidth) / 2;
  const y = (canvasHeight - imageHeight) / 2;

  context.drawImage(image, x, y, imageWidth, imageHeight);
};

const getTextFont = (context, text, maxWidth, maxFontSize) => {
  let fontSize = maxFontSize;

  context.font = `700 ${fontSize}px Open Sans, Arial, sans-serif`;

  while (context.measureText(text).width > maxWidth && fontSize > 28) {
    fontSize -= 2;
    context.font = `700 ${fontSize}px Open Sans, Arial, sans-serif`;
  }

  return fontSize;
};

const drawScoreText = (context, lines, textBox) => {
  const firstLineFontSize = getTextFont(
    context,
    lines[0],
    textBox.width,
    textBox.maxFontSize
  );
  const secondLineFontSize = getTextFont(
    context,
    lines[1],
    textBox.width,
    textBox.maxFontSize + 12
  );
  const lineGap = 10;
  const totalHeight = firstLineFontSize + secondLineFontSize + lineGap;
  const firstLineY =
    textBox.y + textBox.height / 2 - totalHeight / 2 + firstLineFontSize / 2;
  const secondLineY =
    textBox.y + textBox.height / 2 + totalHeight / 2 - secondLineFontSize / 2;

  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillStyle = '#202020';

  context.font = `700 ${firstLineFontSize}px Open Sans, Arial, sans-serif`;
  context.fillText(lines[0], textBox.x + textBox.width / 2, firstLineY, textBox.width);

  context.font = `700 ${secondLineFontSize}px Open Sans, Arial, sans-serif`;
  context.fillText(lines[1], textBox.x + textBox.width / 2, secondLineY, textBox.width);
};

const downloadBlob = (blob) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = 'puntaje-mundial-ideal.png';
  link.click();

  URL.revokeObjectURL(url);
};

const copyText = (text) => {
  if (navigator.clipboard) {
    return navigator.clipboard.writeText(text);
  }

  return Promise.resolve();
};

export const getXShareUrl = ({ url, text, hashtags = [] }) => {
  const params = new URLSearchParams({
    url,
    text,
  });

  if (hashtags.length) {
    params.set('hashtags', hashtags.join(','));
  }

  return `https://twitter.com/intent/tweet?${params.toString()}`;
};

const createScoreImage = async ({ username, score, imageType = 'default' }) => {
  const imageConfig = scoreImages[imageType] || scoreImages.default;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  const backgroundUrl = `${config.basePath}/img/${imageConfig.background}`;
  const lines = getScoreLines(username, score);

  canvas.width = imageConfig.width;
  canvas.height = imageConfig.height;

  try {
    const background = await getImage(backgroundUrl);
    drawCoverImage(context, background, imageConfig.width, imageConfig.height);
  } catch (error) {
    context.fillStyle = '#f1dfc6';
    context.fillRect(0, 0, imageConfig.width, imageConfig.height);
  }

  drawScoreText(context, lines, imageConfig.textBox);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('No se pudo crear la imagen para compartir'));
      }
    }, 'image/png');
  });
};

const ShareScore = ({ userId, username, score }) => {
  const [scoreImageBlobs, setScoreImageBlobs] = useState({});
  const base = `${config.host}${config.basePath}`;
  const shareUrl = `${base}/mundial-2026/prode/`;
  const message = getMessage(score);
  const shareText = `${message} ${shareUrl}`;
  const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(
    shareText
  )}`;
  const imageParams = useMemo(() => ({ username, score }), [username, score]);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      createScoreImage({ ...imageParams, imageType: 'default' }),
      createScoreImage({ ...imageParams, imageType: 'instagram' }),
    ])
      .then(([defaultBlob, instagramBlob]) => {
        if (!cancelled) {
          setScoreImageBlobs({
            default: defaultBlob,
            instagram: instagramBlob,
          });
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [imageParams]);

  const shareNative = async (imageType = 'default', fallback) => {
    const blob = scoreImageBlobs[imageType];
    copyText(shareText).catch(() => {});

    try {
      if (blob) {
        const file = new File([blob], 'puntaje-mundial-ideal.png', {
          type: 'image/png',
        });
        const shareData = {
          title: 'Mi puntaje',
          text: shareText,
          files: [file],
        };

        if (navigator.canShare && navigator.canShare(shareData)) {
          await navigator.share(shareData);
          return;
        }
      }

      if (navigator.share) {
        await navigator.share({
          title: 'Mi puntaje',
          text: shareText,
        });

        return;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
    }

    if (fallback) {
      fallback(blob);
      return;
    }

    if (blob) {
      downloadBlob(blob);
    }
  };

  const shareOnWhatsapp = () => {
    shareNative('default', () => {
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    }).catch(() => {
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    });
  };

  const shareOnInstagram = () => {
    shareNative('instagram', (blob) => {
      if (blob) {
        downloadBlob(blob);
        return;
      }

      createScoreImage({ ...imageParams, imageType: 'instagram' })
        .then(downloadBlob)
        .catch(() => {});
    }).catch(() => {});
  };

  const shareOnX = () => {
    const xShareUrl = getXShareUrl({
      url: shareUrl,
      text: message,
      hashtags: ['MundialIdeal'],
    });

    window.open(xShareUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="share-score" aria-label="Compartir puntaje">
      <button
        type="button"
        className="share-score-button whatsapp"
        onClick={shareOnWhatsapp}
        aria-label="Compartir por WhatsApp"
      >
        <WhatsAppOutlined />
      </button>
      <button
        type="button"
        className="share-score-button x"
        onClick={shareOnX}
        aria-label="Compartir en X"
      >
        <img
          src={xOutlinedNegativeIcon}
          alt=""
          aria-hidden="true"
          width="14"
          height="14"
        />
      </button>
      <button
        type="button"
        className="share-score-button instagram"
        onClick={shareOnInstagram}
        aria-label="Compartir en Instagram"
      >
        <InstagramOutlined />
      </button>
    </div>
  );
};

ShareScore.propTypes = {
  userId: PropTypes.string,
  username: PropTypes.string,
  score: PropTypes.number,
};

ShareScore.defaultProps = {
  userId: '',
  username: '',
  score: undefined,
};

export default ShareScore;
